import { CubeIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

interface CaseProductDonutChartProps {
  data: { [key: string]: number };
  title: string;
}

// Predefined colors for known products
const PRODUCT_COLORS = {
  Essentials: { color: '#3B82F6', gradient: 'essentialsGradient' },
  EHR: { color: '#10B981', gradient: 'ehrGradient' },
  'No Product': { color: '#9CA3AF', gradient: 'noProductGradient' },
} as const;

// Default colors for unknown products
const DEFAULT_COLORS = [
  { color: '#EF4444', gradient: 'defaultGradient1' },
  { color: '#F59E0B', gradient: 'defaultGradient2' },
  { color: '#8B5CF6', gradient: 'defaultGradient3' },
  { color: '#EC4899', gradient: 'defaultGradient4' },
  { color: '#06B6D4', gradient: 'defaultGradient5' },
];

const getProductColor = (product: string, index: number) => {
  if (product in PRODUCT_COLORS) {
    return PRODUCT_COLORS[product as keyof typeof PRODUCT_COLORS];
  }

  return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};

const CaseProductDonutChart = ({ data, title }: CaseProductDonutChartProps) => {
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  const handleMouseEnter = (product: string) => {
    setHoveredSegment(product);
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
  };

  const total = Object.values(data).reduce((sum, value) => sum + value, 0);

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400 text-center">
          <div className="text-lg font-medium mb-2">No Data Available</div>
          <div className="text-sm">No product data found for the selected date range</div>
        </div>
      </div>
    );
  }

  // Calculate segments
  const segments = Object.entries(data)
    .filter(([_, value]) => value > 0)
    .map(([product, value]) => ({
      product,
      value,
      percentage: (value / total) * 100,
      angle: (value / total) * 360,
    }))
    .sort((a, b) => b.value - a.value);

  // Create SVG paths for segments
  const centerX = 140;
  const centerY = 140;
  const outerRadius = 110;
  const innerRadius = 70;

  const createPath = (startAngle: number, endAngle: number) => {
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + outerRadius * Math.cos(startAngleRad);
    const y1 = centerY + outerRadius * Math.sin(startAngleRad);
    const x2 = centerX + outerRadius * Math.cos(endAngleRad);
    const y2 = centerY + outerRadius * Math.sin(endAngleRad);

    const x3 = centerX + innerRadius * Math.cos(endAngleRad);
    const y3 = centerY + innerRadius * Math.sin(endAngleRad);
    const x4 = centerX + innerRadius * Math.cos(startAngleRad);
    const y4 = centerY + innerRadius * Math.sin(startAngleRad);

    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`;
  };

  let currentAngle = -90; // Start from top
  const segmentPaths = segments.map(segment => {
    const angle = (segment.percentage / 100) * 360;
    const path = createPath(currentAngle, currentAngle + angle);
    const result = {
      product: segment.product,
      value: segment.value,
      percentage: segment.percentage,
      path,
    };
    currentAngle += angle;

    return result;
  });

  // Get tooltip position
  const getTooltipPosition = (product: string) => {
    const segment = segments.find(s => s.product === product);
    if (!segment) return { x: 0, y: 0 };

    const segmentIndex = segments.findIndex(s => s.product === product);
    let angle = -90; // Start from top

    for (let i = 0; i < segmentIndex; i++) {
      angle += segments[i].angle;
    }

    angle += segment.angle / 2; // Middle of segment

    const radius = 90;
    const x = 140 + radius * Math.cos((angle * Math.PI) / 180);
    const y = 140 + radius * Math.sin((angle * Math.PI) / 180);

    return { x, y };
  };

  const tooltipPosition = hoveredSegment ? getTooltipPosition(hoveredSegment) : { x: 0, y: 0 };
  const hoveredData = segments.find(s => s.product === hoveredSegment);
  const hoveredPercentage = hoveredData ? hoveredData.percentage : 0;

  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <motion.div
        className="flex items-center justify-center gap-3 mb-6"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <motion.div
          className="p-2.5 rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-yellow-900/20 shadow-lg"
          whileHover={{ scale: 1.1, rotate: -5 }}
          transition={{ duration: 0.3, type: 'spring', stiffness: 200 }}
        >
          <CubeIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
        </motion.div>
        <motion.h3
          className="text-xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-yellow-600 dark:from-amber-400 dark:via-orange-400 dark:to-yellow-400 bg-clip-text text-transparent"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          {title}
        </motion.h3>
      </motion.div>

      <motion.div
        className="relative group z-40 flex justify-center"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.5 }}
      >
        <motion.svg
          width="280"
          height="280"
          viewBox="0 0 280 280"
          className="transform transition-all duration-500"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="essentialsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60A5FA" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
            <linearGradient id="ehrGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34D399" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
            <linearGradient id="noProductGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D1D5DB" />
              <stop offset="100%" stopColor="#9CA3AF" />
            </linearGradient>
            <linearGradient id="defaultGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F87171" />
              <stop offset="100%" stopColor="#EF4444" />
            </linearGradient>
            <linearGradient id="defaultGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            <linearGradient id="defaultGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A78BFA" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
            <linearGradient id="defaultGradient4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F472B6" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
            <linearGradient id="defaultGradient5" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22D3EE" />
              <stop offset="100%" stopColor="#06B6D4" />
            </linearGradient>
          </defs>

          {/* Background circle for shadow effect */}
          <circle
            cx="140"
            cy="140"
            r="110"
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth="2"
            className="opacity-50"
          />

          {/* Render segments */}
          {segmentPaths.map((segment, index) => {
            if (segment.value === 0) return null;

            const colorConfig = getProductColor(segment.product, index);

            return (
              <motion.path
                key={segment.product}
                d={segment.path}
                fill={`url(#${colorConfig.gradient})`}
                className="cursor-pointer filter drop-shadow-lg"
                onMouseEnter={() => handleMouseEnter(segment.product)}
                onMouseLeave={handleMouseLeave}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 1.2, ease: 'easeInOut' }}
                whileHover={{
                  filter: `brightness(1.1) drop-shadow(0 8px 16px ${colorConfig.color}30)`,
                  transition: { duration: 0.2 },
                }}
              />
            );
          })}

          {/* Center text */}
          <motion.text
            x="140"
            y="130"
            textAnchor="middle"
            className="text-2xl font-bold fill-gray-900 dark:fill-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            {total.toLocaleString()}
          </motion.text>
          <motion.text
            x="140"
            y="150"
            textAnchor="middle"
            className="text-sm fill-gray-600 dark:fill-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            TOTAL
          </motion.text>
        </motion.svg>

        {/* Tooltip */}
        <AnimatePresence>
          {hoveredSegment && (
            <motion.div
              className="absolute z-50 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-2 rounded-lg shadow-xl text-sm font-medium pointer-events-none"
              style={{
                left: tooltipPosition.x - 40,
                top: tooltipPosition.y - 40,
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-center">
                <div className="font-semibold">{hoveredSegment}</div>
                <div>{hoveredData?.value.toLocaleString()} cases</div>
                <div className="text-xs opacity-75">{hoveredPercentage.toFixed(1)}%</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Compact Enhanced Legend */}
      <motion.div
        className="mt-6 bg-white/60 dark:bg-gray-800/60 rounded-xl p-4 border border-gray-200/60 dark:border-gray-700/60 shadow-lg backdrop-blur-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {segments.map((segment, index) => {
            if (segment.value === 0) return null;

            const colorConfig = getProductColor(segment.product, index);

            return (
              <motion.div
                key={segment.product}
                className="group flex items-center space-x-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-lg border border-gray-100/80 dark:border-gray-700/80 hover:border-amber-200 dark:hover:border-amber-600/50 hover:shadow-md transition-all duration-200"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.6 + index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
              >
                {/* Color Indicator with Glow */}
                <div className="relative flex-shrink-0">
                  <div
                    className="w-3.5 h-3.5 rounded-full shadow-md border border-white/80 dark:border-gray-600/80"
                    style={{ backgroundColor: colorConfig.color }}
                  />
                  <div
                    className="absolute inset-0 w-3.5 h-3.5 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-200 blur-sm"
                    style={{ backgroundColor: colorConfig.color }}
                  />
                </div>

                {/* Content */}
                <div className="flex flex-col items-start min-w-0 flex-1">
                  <span className="text-gray-900 dark:text-gray-100 font-medium text-sm leading-tight truncate w-full">
                    {segment.product}
                  </span>
                  <div className="flex items-center space-x-1.5 mt-0.5">
                    <span className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                      {segment.value.toLocaleString()}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">cases</span>
                    <span className="text-amber-600 dark:text-amber-400 text-xs font-semibold">
                      ({segment.percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CaseProductDonutChart;
